import { createContext, useContext, useEffect, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);
// Prevent GSAP from speeding up time after the tab was hidden
gsap.ticker.lagSmoothing(0);

const AnimationContext = createContext(null);
export const useAnimation = () => useContext(AnimationContext);

// ── Module-level singletons ────────────────────────────────────────────────
// Plain objects — never cause React re-renders, survive component remounts.

const mouse = { tx: 0, ty: 0, cx: 0, cy: 0 };
const LERP = 0.08;

// Mutable refs touched only by the ticker
let _lenis = null;
let _glowEl = null;

/**
 * The one global tick function added to gsap.ticker.
 * Drives Lenis AND the mouse-glow lerp in a single RAF slot.
 * Uses transform3d so the browser keeps updates on the compositor thread.
 */
function globalTick() {
  // ── Lenis ────────────────────────────────────────────────────────────────
  if (_lenis) _lenis.raf(performance.now());

  // ── Mouse glow ───────────────────────────────────────────────────────────
  if (_glowEl) {
    const dx = mouse.tx - mouse.cx;
    const dy = mouse.ty - mouse.cy;
    // Skip sub-pixel updates to avoid needless GPU uploads
    if (Math.abs(dx) > 0.05 || Math.abs(dy) > 0.05) {
      mouse.cx += dx * LERP;
      mouse.cy += dy * LERP;
      _glowEl.style.transform =
        `translate3d(${mouse.cx}px,${mouse.cy}px,0) translate(-50%,-50%)`;
    }
  }
}

// ── Provider ───────────────────────────────────────────────────────────────
export default function AnimationProvider({ children }) {
  useEffect(() => {
    // Seed mouse at viewport centre so glow doesn't fly in from (0,0)
    mouse.tx = mouse.cx = window.innerWidth / 2;
    mouse.ty = mouse.cy = window.innerHeight / 2;

    const onMove = (e) => {
      mouse.tx = e.clientX;
      mouse.ty = e.clientY;
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    gsap.ticker.add(globalTick);

    return () => {
      gsap.ticker.remove(globalTick);
      window.removeEventListener('mousemove', onMove);
    };
  }, []);

  const registerLenis   = useCallback((l)  => { _lenis  = l;    }, []);
  const unregisterLenis = useCallback(()   => { _lenis  = null; }, []);
  const registerGlow    = useCallback((el) => { _glowEl = el;   }, []);

  return (
    <AnimationContext.Provider value={{ registerLenis, unregisterLenis, registerGlow }}>
      {children}
    </AnimationContext.Provider>
  );
}
